
import { useState, useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { Home, BarChart3, X, Gamepad2, Star } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { getAvailableGames, getLocalizedGameId, getGameByLocalizedId } from "@/lib/games-data"
import { useTranslation } from "@/hooks/use-translation"
import { useFavorites } from "@/context/favorites-context"

interface SidebarProps {
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
}

export function Sidebar({ collapsed, setCollapsed }: SidebarProps) {
  const { t, navigate: localizedNavigate, language } = useTranslation()
  const location = useLocation()
  const [isMobile, setIsMobile] = useState(false)
  const sidebarRef = useRef<HTMLElement>(null)
  const { isFavorite } = useFavorites()

  const availableGames = getAvailableGames(language)
  
  // Separar juegos en favoritos y otros
  const favoriteGames = availableGames.filter(game => isFavorite(game.id))
  const otherGames = availableGames.filter(game => !isFavorite(game.id))

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isMobile && !collapsed) {
      // Use overflow hidden to prevent background scroll
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'

      return () => {
        // Restore body scroll
        document.body.style.overflow = ''
        document.body.style.touchAction = ''
      }
    }
  }, [isMobile, collapsed])

  // Get active game from URL (skip language segment)
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const langSegment = pathSegments[0] || ''
  const isLanguageInUrl = ['en', 'es', 'fr'].includes(langSegment)

  const localizedGameId = isLanguageInUrl
    ? (pathSegments[1] === 'game' ? pathSegments[2] : null)
    : (pathSegments[0] === 'game' ? pathSegments[1] : null)

  // Obtener el juego activo basado en el ID localizado
  const activeGame = localizedGameId ? getGameByLocalizedId(localizedGameId, language) : null
  const isHome = pathSegments.length <= 1 || (isLanguageInUrl && pathSegments.length <= 1)
  const isStats = pathSegments.includes('statistics') || (isLanguageInUrl && pathSegments.includes('statistics'))

  const handleGameClick = (gameId: string) => {
    // Usar el ID localizado para la navegación
    const localizedId = getLocalizedGameId(gameId, language)
    localizedNavigate(`/game/${localizedId}`)
    // Cerrar sidebar en móviles después de seleccionar juego
    if (isMobile) {
      setCollapsed(true)
    }
  }

  return (
    <>
      {/* Backdrop para móviles */}
      {!collapsed && isMobile && (
        <div
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setCollapsed(true)}
        />
      )}

      <aside
        className={cn(
          "flex flex-col border-r bg-card transition-all duration-300 ease-in-out",
          // En móviles: 60% width cuando abierto, hidden cuando cerrado
          // En desktop: w-80 cuando abierto, w-16 cuando cerrado
          collapsed
            ? "w-0 lg:w-16 -translate-x-full lg:translate-x-0"
            : "w-[80%] lg:w-80 translate-x-0",
          // Positioning
          "fixed lg:sticky z-40 lg:z-auto",
          "h-dvh lg:h-screen lg:top-0",
          // Visibility
          collapsed ? "lg:block" : "block"
        )}
        onClick={(e) => e.stopPropagation()}
        ref={sidebarRef}
      >
        <div className="flex h-full w-full flex-col overflow-hidden">
          {/* Header - Fixed at top */}
          {!collapsed && (
            <div className="p-4 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <div
                  className={cn(
                    "flex items-center cursor-pointer hover:opacity-80 transition-opacity",
                    "lg:justify-start",
                    "justify-center"
                  )}
                  onClick={() => localizedNavigate("/")}
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-2">
                    <Gamepad2 className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold">{t("app.title")}</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setCollapsed(true)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Content - Takes all available space with scroll */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
            <div className="p-2">
              {/* Home Button - Hidden on mobile */}
              <div className="mb-4 hidden lg:block">
                <Button
                  variant={isHome ? "secondary" : "ghost"}
                  className="w-full justify-start h-auto p-3"
                  onClick={() => localizedNavigate("/")}
                  aria-label={t("navigation.home")}
                >
                  <div className="flex items-center gap-3 w-full">
                    <Home className="h-4 w-4 flex-shrink-0" />
                    {!collapsed && (
                      <div className="flex-1 text-left">
                        <div className="font-medium">{t("navigation.home")}</div>
                        <div className="text-xs">
                          {t("home.sidebarSubtitle")}
                        </div>
                      </div>
                    )}
                  </div>
                </Button>
              </div>

              {/* Separador */}
              {!collapsed && (
                <div className="mb-4">
                  <div className="text-xs text-muted-foreground px-3 mb-2 font-medium uppercase tracking-wider">
                    {t("home.totalGames", { count: availableGames.length })}
                  </div>
                </div>
              )}

              {/* Favorite Games Section */}
              {favoriteGames.length > 0 && (
                <>
                  {!collapsed && (
                    <div className="mb-2">
                      <div className="text-xs text-muted-foreground px-3 mb-2 font-medium uppercase tracking-wider flex items-center gap-1.5">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        {t("home.favoriteGames")}
                      </div>
                    </div>
                  )}
                  <div className="space-y-1 mb-4">
                    {favoriteGames.map((game) => (
                      <Button
                        key={game.id}
                        variant={activeGame?.id === game.id ? "secondary" : "ghost"}
                        className="w-full justify-start h-auto p-3"
                        onClick={() => handleGameClick(game.id)}
                        aria-label={t(`games.${game.id}.name`)}
                      >
                        <div className="flex items-center gap-3 w-full">
                          <game.icon className="h-4 w-4 flex-shrink-0" />
                          {!collapsed && (
                            <div className="flex-1 text-left">
                              <div className="font-medium">{t(`games.${game.id}.name`)}</div>
                              <div className="text-xs">
                                {t(`games.${game.id}.description`)}
                              </div>
                            </div>
                          )}
                        </div>
                      </Button>
                    ))}
                  </div>
                </>
              )}

              {/* Other Games Section */}
              {otherGames.length > 0 && (
                <>
                  {!collapsed && (
                    <div className="mb-2">
                      <div className="text-xs text-muted-foreground px-3 mb-2 font-medium uppercase tracking-wider">
                        {t("home.otherGames")}
                      </div>
                    </div>
                  )}
                  <div className="space-y-1">
                    {otherGames.map((game) => (
                      <Button
                        key={game.id}
                        variant={activeGame?.id === game.id ? "secondary" : "ghost"}
                        className="w-full justify-start h-auto p-3"
                        onClick={() => handleGameClick(game.id)}
                        aria-label={t(`games.${game.id}.name`)}
                      >
                        <div className="flex items-center gap-3 w-full">
                          <game.icon className="h-4 w-4 flex-shrink-0" />
                          {!collapsed && (
                            <div className="flex-1 text-left">
                              <div className="font-medium">{t(`games.${game.id}.name`)}</div>
                              <div className="text-xs">
                                {t(`games.${game.id}.description`)}
                              </div>
                            </div>
                          )}
                        </div>
                      </Button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer - Fixed at bottom */}
          {!collapsed && (
            <div className="flex flex-col gap-2 p-2 flex-shrink-0 border-t">
              <Button
                variant={isStats ? "secondary" : "ghost"}
                className="w-full justify-start h-auto p-3"
                onClick={() => {
                  localizedNavigate("/statistics")
                  // Cerrar sidebar en móviles después de seleccionar estadísticas
                  if (isMobile) {
                    setCollapsed(true)
                  }
                }}
                aria-label={t("navigation.statistics")}
              >
                <div className="flex items-center gap-3 w-full">
                  <BarChart3 className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{t("navigation.statistics")}</div>
                    <div className="text-xs">
                      {t("navigation.statisticsDesc")}
                    </div>
                  </div>
                </div>
              </Button>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}
